import { useCallback } from 'react';
import { useQueryClient, type Query } from '@tanstack/react-query';
import { useDebouncedQueryInvalidation } from 'src/modules/shared/hooks/query/use-debounced-query-invalidation';
import { refreshConversationSearch } from 'src/modules/shared/communications/services/conversation-search-refresh.service';

const INVALIDATION_DELAY_MS = 600;

const isMessageDetailQuery = (query: Query): boolean => {
  const { queryKey } = query;

  return (
    Array.isArray(queryKey) &&
    queryKey[0] === 'messaging/messages' &&
    typeof queryKey[1] === 'string'
  );
};

/**
 * Selects the Communications HTTP views that recover when realtime cannot reconcile.
 * @returns Recovery action, optionally excluding canonical conversation reads.
 */
export const useCommunicationsQueryRecovery = () => {
  const queryClient = useQueryClient();
  const scheduleInvalidation = useDebouncedQueryInvalidation(
    INVALIDATION_DELAY_MS
  );
  return useCallback(
    (includeCanonical = true) => {
      scheduleInvalidation('timeline', {
        queryKey: ['crm/timeline-items/search'],
        exact: false,
      });
      void refreshConversationSearch(queryClient);
      if (includeCanonical) {
        scheduleInvalidation('conversations', {
          queryKey: ['crm/funnel-conversations'],
          exact: false,
        });
      }
      scheduleInvalidation('message-details', {
        predicate: isMessageDetailQuery,
      });
      scheduleInvalidation('channel-sessions', {
        queryKey: ['crm/funnel-channel-sessions'],
        exact: false,
      });
    },
    [queryClient, scheduleInvalidation]
  );
};
